/**
 * CARA Form Component (Customer Account Risk Assessment)
 * Based on Figma: https://www.figma.com/design/8nBzg5I71TF3EWyr6NcpVa/Fiat?node-id=6053-65231
 */
import { useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/ui/form';
import { RadioGroup, RadioGroupItem } from '@components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select';
import { COUNTRY_OPTIONS } from '@shared-web/constants/country';

import { useCaraOptions } from '../../hooks/useCaraOptions';

const formSchema = z.object({
  annualTransactionVolume: z
    .number()
    .min(1, { message: 'Annual transaction volume is required' }),
  annualIncome: z.number().min(1, { message: 'Annual income is required' }),
  occupation: z.number().min(1, { message: 'Occupation is required' }),
  sourceOfWealth: z
    .string()
    .min(1, { message: 'Source of wealth is required' }),
  sourceOfFundsCountry: z
    .string()
    .min(1, { message: 'Source of funds country is required' }),
  transactionCountry: z
    .string()
    .min(1, { message: 'Transaction country is required' }),
  citizenshipByInvestment: z.enum(['2', '1'], {
    message: 'Please select an option',
  }),
});

type FormSchema = z.infer<typeof formSchema>;

export enum CaraTypeEnum {
  All = 'all',
  One = 'one',
}

export interface CaraFormProps {
  readonly caraType: CaraTypeEnum;
  readonly currency?: string;
  readonly onSubmit: (data: FormSchema) => void | Promise<void>;
  readonly onClose?: () => void;
  readonly defaultValues?: Partial<FormSchema>;
  readonly isSubmitting?: boolean;
}

export function CaraForm({
  caraType = CaraTypeEnum.All,
  currency = 'EUR',
  onSubmit,
  onClose,
  defaultValues,
  isSubmitting = false,
}: CaraFormProps) {
  const { t } = useTranslation('fiat');
  const {
    employmentStatusOptions,
    annualIncomeOptions,
    annualTransactionOptions,
  } = useCaraOptions();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      annualTransactionVolume: defaultValues?.annualTransactionVolume ?? 0,
      annualIncome: defaultValues?.annualIncome,
      occupation: defaultValues?.occupation,
      sourceOfWealth: defaultValues?.sourceOfWealth,
      sourceOfFundsCountry: defaultValues?.sourceOfFundsCountry,
      transactionCountry: defaultValues?.transactionCountry,
      citizenshipByInvestment: defaultValues?.citizenshipByInvestment,
    },
  });

  // Source of wealth options
  const sourceOfWealthOptions = useMemo(
    () => [
      { text: t('edd.sou1'), value: 1 },
      { text: t('edd.sou2'), value: 2 },
      { text: t('edd.sou3'), value: 3 },
      { text: t('edd.sou4'), value: 4 },
      { text: t('edd.sou5'), value: 5 },
      { text: t('edd.sou6'), value: 6 },
      { text: t('edd.sou9'), value: 7 },
      { text: t('edd.sou7'), value: 8 },
      { text: t('edd.sou10'), value: 9 },
      { text: t('edd.sou8'), value: 10 },
    ],
    [t],
  );

  return (
    <div className="w-[670px]">
      {/* Header */}
      <h3 className="text-foreground text-h3">
        Provide additional information to activate {currency} cash transfers
      </h3>

      {/* Description */}
      <p className="text-muted-foreground text-body1 mt-3 mb-8">
        To comply with financial regulations, we need a few additional details
        before enabling cash transfers. This helps keep your account secure and
        ensures smooth processing of your transactions.
      </p>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6"
        >
          {/* Row 1: Annual Transaction Volume & Annual Income */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {/* Labels row */}
            <FormLabel>
              What is your expected annual transaction volume (in {currency})?
            </FormLabel>
            <FormLabel>What is your annual income (in {currency})?</FormLabel>

            {/* Inputs row */}
            <FormField
              control={form.control}
              name="annualTransactionVolume"
              render={({
                field,
              }: {
                field: ControllerRenderProps<
                  FormSchema,
                  'annualTransactionVolume'
                >;
              }) => (
                <FormItem className="space-y-0">
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value ? String(field.value) : undefined}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {annualTransactionOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {caraType === CaraTypeEnum.All && (
              <FormField
                control={form.control}
                name="annualIncome"
                render={({
                  field,
                }: {
                  field: ControllerRenderProps<FormSchema, 'annualIncome'>;
                }) => (
                  <FormItem className="space-y-0">
                    <Select
                      onValueChange={(value) => field.onChange(Number(value))}
                      value={field.value ? String(field.value) : undefined}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {annualIncomeOptions.map((option) => (
                          <SelectItem
                            key={option.value}
                            value={String(option.value)}
                          >
                            {option.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>

          {/* Row 2: Occupation & Source of Wealth */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {/* Labels row */}
            <FormLabel>What is your occupation?</FormLabel>
            <FormLabel>What are the main sources of your wealth?</FormLabel>

            {/* Inputs row */}
            <FormField
              control={form.control}
              name="occupation"
              render={({
                field,
              }: {
                field: ControllerRenderProps<FormSchema, 'occupation'>;
              }) => (
                <FormItem className="space-y-0">
                  <Select
                    onValueChange={(value) => field.onChange(Number(value))}
                    value={field.value ? String(field.value) : undefined}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employmentStatusOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sourceOfWealth"
              render={({
                field,
              }: {
                field: ControllerRenderProps<FormSchema, 'sourceOfWealth'>;
              }) => (
                <FormItem className="space-y-0">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {sourceOfWealthOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={String(option.value)}
                        >
                          {option.text}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Row 3: Source of Funds Country & Transaction Country */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-2">
            {/* Labels row */}
            <FormLabel>
              What is the primary country where your source of funds is derived
              from?
            </FormLabel>
            <FormLabel>
              What is the primary country you plan to transact with?
            </FormLabel>

            {/* Inputs row */}
            <FormField
              control={form.control}
              name="sourceOfFundsCountry"
              render={({
                field,
              }: {
                field: ControllerRenderProps<
                  FormSchema,
                  'sourceOfFundsCountry'
                >;
              }) => (
                <FormItem className="space-y-0">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map(
                        (country: { text: string; value: string }) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.text}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="transactionCountry"
              render={({
                field,
              }: {
                field: ControllerRenderProps<FormSchema, 'transactionCountry'>;
              }) => (
                <FormItem className="space-y-0">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map(
                        (country: { text: string; value: string }) => (
                          <SelectItem key={country.value} value={country.value}>
                            {country.text}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Citizenship By Investment Question */}
          <FormField
            control={form.control}
            name="citizenshipByInvestment"
            render={({
              field,
            }: {
              field: ControllerRenderProps<
                FormSchema,
                'citizenshipByInvestment'
              >;
            }) => (
              <FormItem>
                <FormLabel>
                  Have you obtained your nationality through the "Citizenship By
                  Investment Scheme"?
                </FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="2" id="citizenship-yes" />
                      <label
                        htmlFor="citizenship-yes"
                        className="text-body1 cursor-pointer"
                      >
                        Yes
                      </label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="citizenship-no" />
                      <label
                        htmlFor="citizenship-no"
                        className="text-body1 cursor-pointer"
                      >
                        No
                      </label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="secondary"
              className="flex-1"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Continue'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
